<template>
    <div class="boxTR p-3 d-flex flex-column-reverse flex-md-row justify-content-between align-items-start align-items-md-center">
        <!-- Button: Weitere Person versichern -->
        <button 
            v-if="kannWeiterePersonHinzufuegen"
            type="button" 
            class="font-base-links btn btn-vigo-1-500 rounded rounded-pill d-flex align-items-center white-space-nowrap mt-3 mt-md-0"
            @click="$emit('weitere-person')"
        >
            <plus_icon class="me-2"></plus_icon>
            Weitere Person versichern
        </button>
        <div v-else></div>
        
        <!-- Gesamtbeträge -->
        <div class="text-end text-md-end">
            <template v-if="mindestlaufzeit.monate > 0 && hatGueltigePersonen">
                <div class="d-flex flex-row align-items-center">
                    <div>
                        Erstbeitrag: <strong>{{ gesamtErstbeitragStr }}</strong>
                    </div>
                    <div class="ms-2 pb-1"><MindestvertragsdauerPopover /></div>
                </div>
            </template>
            <template v-else-if="hinweistext">
                <strong>{{ hinweistext }}</strong>
            </template>
        </div>
    </div>    
</template>

<script setup>
import { computed } from 'vue';
import plus_icon from "@/components/icons/plus_icon.vue";
import { useRechnerStore } from '@/stores/rechner';
import { 
    berechneMindestlaufzeit, 
    berechneAR20Beitraege,
    formatiereBeitrag,
    isValidDateInPast,
    isValidVersicherungsbeginnAR20
} from '@/helpers/hilfsfunktionen';
import MindestvertragsdauerPopover from '@/components/ar20/ar20_mindestvertragsdauer_popover.vue';
const props = defineProps({
    maxPersonen: {
        type: Number,
        default: 10
    }
});

defineEmits(['weitere-person']);

const store = useRechnerStore();

// Prüft ob der Versicherungsbeginn gültig ist (heute bis +6 Monate)
const hatGueltigeVersicherungsbeginn = computed(() => {
    return isValidVersicherungsbeginnAR20(store.versicherungsbeginn);
});

// Mindestlaufzeit basierend auf globalem Versicherungsbeginn
const mindestlaufzeit = computed(() => {
    if (!hatGueltigeVersicherungsbeginn.value) {
        return { monate: 0, von: null, bis: null, vonStr: '', bisStr: '' };
    }
    return berechneMindestlaufzeit(store.versicherungsbeginn);
});

// Prüft ob weitere Person hinzugefügt werden kann
const kannWeiterePersonHinzufuegen = computed(() => {
    return store.items.length < props.maxPersonen;
});

// Prüft ob alle Personen ein gültiges Geburtsdatum haben
const hatGueltigePersonen = computed(() => {
    return store.items.every(item => 
        item.tarif6 === 1 && isValidDateInPast(item.geburtstag)
    );
});

// Hinweistext wenn Daten fehlen oder ungültig
const hinweistext = computed(() => {
    if (!store.versicherungsbeginn) {
        return 'Bitte den Versicherungsbeginn auswählen';
    }
    if (!hatGueltigeVersicherungsbeginn.value) {
        return 'Bitte gültigen Versicherungsbeginn eingeben';
    }
    const ohneGeburtsdatum = store.items.filter(item => 
        item.tarif6 === 1 && !isValidDateInPast(item.geburtstag)
    );
    if (ohneGeburtsdatum.length > 0) {
        return 'Bitte immer ein Geburtsdatum eingeben';
    }
    return '';
});

// Berechnet Gesamt-Erstbeitrag (Summe aller Erstbeiträge)
const gesamtErstbeitrag = computed(() => {
    let summe = 0;
    store.items.forEach(item => {
        if (item.tarif6 === 1 && item.tarif6beitrag > 0) {
            const beitraege = berechneAR20Beitraege(item.tarif6beitrag, store.versicherungsbeginn);
            summe += beitraege.erstbeitrag;
        }
    });
    return summe;
});

// Berechnet Gesamt-Jahresbeitrag (Summe aller Jahresbeiträge)
const gesamtJahresbeitrag = computed(() => {
    let summe = 0;
    store.items.forEach(item => {
        if (item.tarif6 === 1 && item.tarif6beitrag > 0) {
            summe += item.tarif6beitrag;
        }
    });
    return summe;
});

// Formatierte Strings
const gesamtErstbeitragStr = computed(() => {
    return formatiereBeitrag(gesamtErstbeitrag.value);
});

const gesamtJahresbeitragStr = computed(() => {
    return formatiereBeitrag(gesamtJahresbeitrag.value);
});
</script>

<style scoped>
.white-space-nowrap {
    white-space: nowrap !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
    hyphens: none !important;
}
</style>
